import { Color, Spacing } from '@persona-id/design-tokens';
import { DatabaseIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import Fuse from 'fuse.js';
import { compact, uniq } from 'lodash/fp';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFieldArray, useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import ModalWrapper from 'dashboard/components/ModalWrapper';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import {
  DEFAULT_FIELD_SCHEMA,
  EditorableSchema,
  EditorableSchemaWithId,
  FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY,
  RelationSchemaType,
} from 'dashboard/components-lib/FieldSchemaEditor/constants';
import EmptyState from 'dashboard/components-lib/FieldSchemaEditor/EmptyState';
import { Column } from 'dashboard/components-lib/FieldSchemaEditor/table';
import {
  fieldTypeFilterOptions,
  getAllFields,
} from 'dashboard/components-lib/FieldSchemaEditor/utils';
import FieldSchemaContextMenu from 'dashboard/components-lib/FieldSchemaEditorForm/FieldSchemaContextMenu';
import FormFieldRow from 'dashboard/components-lib/FieldSchemaEditorForm/FormFieldRow';
import FormFieldSchemaPanel from 'dashboard/components-lib/FieldSchemaEditorForm/FormFieldSchemaPanel';
import { LabelColumn } from 'dashboard/components-lib/FieldSchemaEditorForm/shared';
import { getDefaultValueDisplay } from 'dashboard/components-lib/FieldSchemaEditorForm/utils';
import { validateAllSchemas } from 'dashboard/components-lib/FieldSchemaEditorForm/validation-form';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import SearchInput from 'dashboard/components-lib/SearchInput';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import Table, { TableHead, TableHeaderCell, TableRow } from 'design-system/components/Table';
import { FieldSchema, FieldSchemaType } from 'lib/fields';

const TypeColumn = styled.div`
  display: flex;
  align-items: center;
  height: ${Spacing[5]};
`;

const DEFAULT_COLUMNS: Column[] = [
  {
    header: 'Label',
    generateContent: ({ field }) => <LabelColumn key={field.label}>{field.label}</LabelColumn>,
  },
  {
    header: 'Type',
    width: '150px',
    generateContent: ({ field }) => {
      if (!field.type) return;

      // TODO(zixiao): show object type name for relation type description
      return (
        <TypeColumn>
          <Sticker>{FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[field.type]}</Sticker>
          {field.type === FieldSchemaType.Array && field.config['item-schema'] && (
            <>
              <Spacer width={0.5} />
              of
              <Spacer width={0.5} />
              <Sticker>
                {
                  FIELD_SCHEMA_EDITOR_SUPPORTED_FIELD_TYPE_OPTIONS_DISPLAY[
                    field.config['item-schema'].type
                  ]
                }
              </Sticker>
            </>
          )}
        </TypeColumn>
      );
    },
  },
  {
    header: 'Default value',
    width: '120px',
    generateContent: ({ field }) =>
      field.type !== RelationSchemaType.RelationSchema
        ? getDefaultValueDisplay(field['default-value'])
        : '--',
  },
];

const TitleContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.8fr;
  grid-gap: ${Spacing[3]};
  width: 100%;
  align-items: center;
  margin-bottom: ${Spacing[4]};
`;

const StickyTableHead = styled(TableHead).attrs(() => ({ sticky: true }))`
  z-index: 2;
`;

interface FieldSchemaEditorV2Props {
  fieldNamePrefix: string;
  nullState: React.ReactNode;
  rightPanelPortalId: string;
  title?: string;
  additionalColumns?: Column[];
  additionalHeaderActions?: React.ReactNode;
  systemFieldSchemas?: FieldSchema[];
  allowRelation?: boolean;
  editable: boolean;
  canDeactivate: (fieldSchema: EditorableSchema, isNested: boolean) => boolean;
  canDelete: (fieldSchema: EditorableSchema, isNested: boolean) => boolean;
  canEdit: (fieldSchema: EditorableSchema) => boolean;
  renderAdditionalPanelContent?: (
    fieldSchema: EditorableSchema,
    fieldName: string,
    formContext: ReturnType<typeof useFormContext>,
  ) => React.ReactNode;
  showActive?: boolean;
  showInactive?: boolean;
}

const FieldSchemaEditorV2: React.FC<FieldSchemaEditorV2Props> = ({
  fieldNamePrefix,
  nullState,
  rightPanelPortalId,
  title,
  additionalColumns = [],
  additionalHeaderActions,
  systemFieldSchemas = [],
  allowRelation,
  editable,
  showActive = true,
  showInactive = false,
  canDeactivate,
  canDelete,
  canEdit,
  renderAdditionalPanelContent,
}) => {
  const [selectedFieldSchemaPath, setSelectedFieldSchemaPath] = useState<string>('');

  const { clearErrors, getValues, setError, watch, setValue } = useFormContext();
  const {
    fields,
    append,
    insert,
    remove,
    // @ts-expect-error - useFieldArray fails to compute type because EditorableSchema is infinitely nestable
  } = useFieldArray<{ [key: string]: EditorableSchema[] }>({
    name: fieldNamePrefix as `${typeof fieldNamePrefix}`,
    rules: {
      validate: (value: EditorableSchema[]) => {
        const errKeys = validateAllSchemas(value);
        errKeys.forEach((errKey) => {
          setError(`${fieldNamePrefix}.${errKey}` as `${typeof fieldNamePrefix}.${number}`, {
            type: 'custom',
          });
        });

        return errKeys.length === 0;
      },
    },
  });
  const deactivateFieldSchemaKeys = watch('deactivateFieldSchemaKeys');

  const [numInserts, setNumInserts] = useState(0);
  const [confirmDeactivateFieldInfo, setConfirmDeactivateFieldInfo] = useState<{
    fieldSchema: EditorableSchema;
    fieldSchemaPath: string;
  } | null>(null);

  const fieldSchemasLiveUpdated: EditorableSchema[] = watch(fieldNamePrefix);

  const [searchTerm, setSearchTerm] = useState<string>('');
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const hasUsedFilters = searchTerm !== '' || typeFilters.length > 0;

  // NOTE: If we need tokenized search terms, consder the following approach in this Github comment:
  // https://github.com/krisk/Fuse/issues/235#issuecomment-850269634
  // TODO: Move this out into it's own hook
  // We keep a Fuse instance around to avoid re-creating it on every render
  const fuseKeys = [
    'key',
    ['config', 'item-schemas', 'key'],
    ['config', 'item-schema', 'config', 'item-schemas', 'key'],
    'label',
    ['config', 'item-schemas', 'label'],
    ['config', 'item-schema', 'config', 'item-schemas', 'label'],
  ];

  // Fuse results contain only top level keys
  const fuse = useMemo(
    () =>
      new Fuse(fieldSchemasLiveUpdated as EditorableSchemaWithId[], {
        threshold: 0.0,
        shouldSort: false,
        ignoreLocation: true,
        minMatchCharLength: 1,
        findAllMatches: true,
        keys: fuseKeys,
      }),
    [fieldSchemasLiveUpdated, fuseKeys],
  );

  const relevantResultsWithIndices = useMemo(() => {
    const searchResults = (
      searchTerm !== ''
        ? fuse.search(searchTerm).map(({ item, refIndex }) => [item, refIndex])
        : fieldSchemasLiveUpdated.map((item, index) => [item, index])
    ) as Array<[EditorableSchemaWithId, number]>;

    return searchResults.filter(
      ([{ type }]) => typeFilters.length === 0 || typeFilters.includes(type),
    );
  }, [fieldSchemasLiveUpdated, searchTerm, typeFilters]) as Array<[EditorableSchemaWithId, number]>;

  const relevantResultIndices = relevantResultsWithIndices.map(([, index]) => index);

  const columns = [...DEFAULT_COLUMNS, ...additionalColumns];
  if (editable) {
    columns.push({
      header: '',
      width: '60px',
      generateContent: (props) => {
        const { field, fieldName, nestLevel } = props;
        let key = field.key;
        if (field.type === FieldSchemaType.Hash) {
          key = `${field.key}-${field.config['item-schemas'].length}`;
        } else if (
          field.type === FieldSchemaType.Array &&
          field.config['item-schema']?.type === FieldSchemaType.Hash
        ) {
          key = `${field.key}-${field.config['item-schema'].config['item-schemas'].length}`;
        }

        const isNested = nestLevel > 0;

        // key ensures context menu properly dismisses for each action
        return (
          <div key={key} style={{ marginLeft: 'auto' }}>
            <FieldSchemaContextMenu
              {...props}
              onEditFieldSchema={() => {
                editFieldSchema(fieldName);
              }}
              onDeactivateFieldSchema={
                canDeactivate(field, isNested)
                  ? () =>
                      setConfirmDeactivateFieldInfo({
                        fieldSchema: field,
                        fieldSchemaPath: fieldName,
                      })
                  : undefined
              }
              onDeleteFieldSchema={
                canDelete(field, isNested)
                  ? () => {
                      deleteFieldSchema(fieldName);
                    }
                  : undefined
              }
            />
          </div>
        );
      },
    });
  }

  const rightPanelElement = document.getElementById(rightPanelPortalId);
  const addFieldSchema = () => {
    append(DEFAULT_FIELD_SCHEMA);
    setSelectedFieldSchemaPath(`${fieldNamePrefix}.${fields.length}`);
  };
  const deleteFieldSchema = (fieldSchemaPath: string) => {
    const pathArray = fieldSchemaPath.split('.');
    // Check if path is in the form of [fieldNamePrefix].[number] by checking if the split pathArray
    // hash longer than the split fieldNamePrefix by 1
    const isTopLevelSchema = pathArray.length - fieldNamePrefix.split('.').length === 1;
    if (isTopLevelSchema) {
      // Simply remove that top-level index
      remove(parseInt(pathArray[pathArray.length - 1]));
    } else {
      // Get the value of the parent array of the selected nested schema
      const schemaParentPath = pathArray.slice(0, -1).join('.');
      const pathArrayValue = getValues(schemaParentPath);
      // Remove the deleted index and update that parent array
      pathArrayValue.splice(parseInt(pathArray[pathArray.length - 1]), 1);
      // FIXME: Note that if you delete a nested schema from a parent with nested schemas of length 1,
      //  there is no way to add another element to that parent's now-empty nested schema.
      //  You have to delete and re-add the parent schema. This UX should be improved.
      setValue(schemaParentPath, pathArrayValue, { shouldDirty: true });
    }

    clearErrors(fieldSchemaPath);

    if (fieldSchemaPath === selectedFieldSchemaPath) {
      // Close the panel on delete of the current selected schema
      setSelectedFieldSchemaPath('');
    }
  };
  const editFieldSchema = (fieldSchemaPath: string) => {
    setSelectedFieldSchemaPath(fieldSchemaPath);
  };
  const closePanel = () => setSelectedFieldSchemaPath('');

  const relevantFieldsWithIndex = compact(
    fields.map((fieldSchema, index) => {
      if (
        relevantResultIndices.includes(index) &&
        ((fieldSchema.config['deactivated-at'] == null && showActive) ||
          (fieldSchema.config['deactivated-at'] != null && showInactive))
      ) {
        return { fieldSchema, index };
      } else {
        return null;
      }
    }),
  );

  const typeFilterOptions = fieldTypeFilterOptions(
    uniq(compact(relevantFieldsWithIndex.map(({ fieldSchema: { type } }) => type))),
  );
  const typeFilterLabelTextMapper = useLabelTextMapper(typeFilterOptions, 'types', {
    emptyIsAll: true,
  });

  return (
    <>
      <Card>
        <ModalWrapper
          isOpen={confirmDeactivateFieldInfo != null}
          handleCloseModal={() => setConfirmDeactivateFieldInfo(null)}
        >
          <ConfirmationModal
            cancelAction={() => setConfirmDeactivateFieldInfo(null)}
            confirmAction={() => {
              if (confirmDeactivateFieldInfo == null) {
                return;
              }
              if (confirmDeactivateFieldInfo.fieldSchemaPath === selectedFieldSchemaPath) {
                // Close the panel on deactivation of the current selected schema
                setSelectedFieldSchemaPath('');
              }
              setValue(
                'deactivateFieldSchemaKeys',
                [...deactivateFieldSchemaKeys, confirmDeactivateFieldInfo.fieldSchema.key],
                {
                  shouldDirty: true,
                },
              );
            }}
            header={`Are you sure you want to deactivate field ${confirmDeactivateFieldInfo?.fieldSchema?.key}?`}
            confirmActionText='Deactivate Field'
          >
            Deactivating a field that is used within Persona may cause errors. Deactivation is not
            confirmed until Save.
          </ConfirmationModal>
        </ModalWrapper>
        <Card.Body>
          <Card.Title
            actions={
              editable && (
                <>
                  <Button
                    id='field-schema-editor__create-field-button'
                    variant='basic'
                    size='md'
                    disabled={!editable}
                    prefixIcon={<PlusIcon />}
                    onClick={addFieldSchema}
                  >
                    Add Field
                  </Button>
                  {additionalHeaderActions}
                </>
              )
            }
          >
            {title ?? 'Fields'}
          </Card.Title>
          <TitleContainer>
            <SearchInput
              size='md'
              title={null}
              value={searchTerm}
              handleInput={setSearchTerm}
              placeholder='Search for a field'
            />
            <MultiSelect
              size='md'
              value={typeFilters}
              selectOptionValues={typeFilterOptions}
              labelTextMapper={typeFilterLabelTextMapper}
              onChange={setTypeFilters}
              disabled={typeFilterOptions.length === 0}
            />
          </TitleContainer>
          <Table style={{ tableLayout: 'fixed' }}>
            <StickyTableHead>
              <TableRow>
                <TableHeaderCell>Key</TableHeaderCell>
                {columns.map((column, columnIndex) => {
                  return (
                    <TableHeaderCell
                      key={`${columnIndex}-${column.header}-header`}
                      style={{ width: column.width ?? 'auto' }}
                    >
                      {column.header}
                    </TableHeaderCell>
                  );
                })}
              </TableRow>
            </StickyTableHead>
            {/* loop through all fields and only render if relevant result from search.
                we maintain indexes based on the original data structure to ensure react hook form
                knows where to update values
              */}
            {relevantFieldsWithIndex.map(({ fieldSchema: item, index }) => {
              const fieldName = `${fieldNamePrefix}.${index}`;
              const fieldSchema = watch(fieldName);

              // Hide schemas selected to be deactivated
              if (deactivateFieldSchemaKeys.includes(fieldSchema.key)) {
                return null;
              }

              return (
                <FormFieldRow
                  key={item.id}
                  fieldNamePrefix={fieldNamePrefix}
                  fieldIndex={index}
                  fieldSchema={fieldSchema}
                  columns={columns}
                  selectedFieldSchemaPath={selectedFieldSchemaPath}
                  editable={editable}
                  onInsertSibling={(index, fieldSchema) => {
                    insert(index, fieldSchema);
                    setNumInserts((count) => count + 1);
                  }}
                  selectFieldSchema={setSelectedFieldSchemaPath}
                />
              );
            })}
          </Table>
          {relevantFieldsWithIndex.length === 0 && (
            <EmptyState productIcon={<DatabaseIcon size={48} color={Color.BorderIconSecondary} />}>
              {hasUsedFilters ? 'No fields found. Please try another search.' : nullState}
            </EmptyState>
          )}
        </Card.Body>
      </Card>
      {rightPanelElement != null &&
        selectedFieldSchemaPath != null &&
        createPortal(
          <FormFieldSchemaPanel
            // There is an edge where if you insert a new field schema above the currently
            // selected row, then correctly `selectedFieldSchemaPath` correctly does not change
            // as the new field schema is in the same position. However, since the value
            // of the selected field schema is different, we need to ensure that the
            // panel is re-rendered. This re-rendering somehow does not reliably happen,
            // so we force it by ensuring the key is changed in this particular edge case
            key={selectedFieldSchemaPath + numInserts.toString()}
            fieldName={selectedFieldSchemaPath}
            allFields={getAllFields(fieldSchemasLiveUpdated)}
            allowRelation={allowRelation}
            editingEnabled={editable}
            canEdit={canEdit}
            onClose={closePanel}
            onDelete={() => deleteFieldSchema(selectedFieldSchemaPath)}
            renderAdditionalPanelContent={renderAdditionalPanelContent}
            systemFieldSchemas={systemFieldSchemas}
          />,
          rightPanelElement,
        )}
    </>
  );
};

export default FieldSchemaEditorV2;
